<template>
  <div v-if="colors" class="attribute-color">
    <input id="product-color" type="hidden" :value="currentColorLabel" />
    <div class="buying-zone__form-label-group">
      <div class="variantName">
        <span v-html="label" />
        <span>/</span>
        <span qaid="product-color-selected" class="text-gray-60 italic" v-html="currentColorLabel"></span
        ><span v-if="currentFinishValue">; <template v-html="currentFinishLabel" /> / </span>
        <span v-if="currentFinishValue" class="text-gray-60 italic" v-html="currentFinishValue" />
      </div>
      <pdp-stock-status
        v-if="renderStockStatus && currentColor && (!children || !children.length)"
        :stock-message="stockMessage"
      />
    </div>
    <swatches
      :colors="colors"
      :radio-name="`swatches-${attribute.code}`"
      :active-color="currentColor"
      @color-changed="colorChanged"
    >
    </swatches>
    <div v-if="currentColor && children && children.length > 0">
      <component
        :is="getAttributeComponent(children)"
        :base-properties="baseProperties"
        :product="product"
        :init-attribute-selection="initAttributeSelection"
        :label="getAttributeLabel(children)"
        :render-stock-status="renderStockStatus"
        :stock-message="stockMessage"
        :is-loading-stock-data="isLoadingStockData"
        :attribute="children"
        @color-changed="
          (color) => {
            $emit('color-changed', color);
          }
        "
        @product-selected="productSelected"
      >
      </component>
    </div>
  </div>
</template>
<script>
import {Attribute} from '@/components/containers/pdp/attributes/mixins/attribute';
import {AttributeRenderer} from '@/components/containers/pdp/attributes/mixins/attribute-renderer';
import Swatches from '@/components/Swatches';
import get from 'lodash/get';
import PdpStockStatus from '@/components/containers/pdp/PdpStockStatus';

export default {
  name: 'AttributeColor',
  components: {Swatches, PdpStockStatus},
  mixins: [Attribute, AttributeRenderer],
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentColor: '',
    };
  },
  computed: {
    children() {
      const children = get(this.getActiveItem(), 'children', []);

      return children.sort((a, b) => {
        return a.weight > b.weight ? 1 : a.weight < b.weight ? -1 : 0;
      });
    },

    /**
     * Get human label for the current color.
     * @return {*}
     */
    currentColorLabel() {
      if (this.currentColor) {
        return get(this.getActiveItem(), 'options.displayName', '');
      }
      return '';
    },

    /**
     * Retrieve list of colors for swatches.
     * @return {Array}
     */
    colors() {
      if (!this.attribute) {
        return [];
      }

      let result = [];
      const metaItemCodes = this.getAttributeMetadataCodes();
      this.attribute.forEach((item) => {
        if (metaItemCodes.includes(item.code)) {
          result.push(get(item, 'options.colorSwatchName'));
        }
      });

      return result;
    },
  },
  watch: {
    /**
     * Watches change of the v-model.
     * @param value
     */
    value(value) {
      if (value && this.colors.indexOf(this.value)) {
        this.currentColor = value;
      }

      this.fireSelectedProduct();
    },
  },
  created() {
    this.getDefaultColorCode();
  },
  methods: {
    /**
     * Initialize default color from given v-model or selects the first one in the list if nothing has been specified.
     */
    getDefaultColorCode() {
      if (!this.colors.length) {
        return;
      }

      const initColor = get(this.initAttributeSelection, this.attribute[0].code);
      if (initColor) {
        this.currentColor = initColor;
      } else if (!this.value || !this.colors.indexOf(this.value)) {
        this.currentColor = this.colors[0];
      } else {
        this.currentColor = this.value;
      }
    },

    fireSelectedProduct() {
      const children = get(this.getActiveItem(), 'children');
      if (children && children.length > 0) {
        return;
      }

      const currentProduct = get(this.getActiveItem(), 'options');
      if (currentProduct) {
        this.productSelected(currentProduct);
      }
    },

    /**
     * Event handler, fires when user changes color via swatches.
     * @param newColor
     */
    colorChanged(newColor) {
      this.currentColor = newColor;
      this.$emit('color-changed', newColor);
      this.fireSelectedProduct();
    },

    /**
     * Get currently selected attribute.
     */
    getActiveItem() {
      return this.attribute.find((color) => color.options.colorSwatchName === this.currentColor);
    },
  },
};
</script>
<style lang="scss" scoped>
.attribute-color {
  .swatches {
    justify-content: left;

    &,
    ul {
      margin-left: 0;
      margin-right: 0;
    }
  }
}
</style>
